'use client';

import React, { useEffect, useState, useTransition } from 'react';
import { Formik, useFormikContext } from 'formik';
import {
    DropdownSelect,
    RadioButtonGroup,
    FormDivider,
    TextInput,
    DateOfBirth,
    HoneypotField,
} from '@/components/formComponents';
import { HONEYPOT_FIELD } from '@/components/formComponents/HoneypotField';
import { submitQuoteForm } from '@/actions/quote-form';
import { PageMainCard } from '@/components';
import { toast } from 'react-hot-toast';
import { Button } from '@/components';
import validationSchema from '@/validation/quoteFormValidation';
import ValuesDisplay from '../formComponents/ValuesDisplay';
import { daysBetween } from '@/utils/quoteForm/formCalculations';
import { format } from 'date-fns';
import { FormRow, NonFormRow } from '@/components/formComponents/helpers';
import { VehicleProps } from '@/types/types';
import { FormSkeleton } from '../Skeletons/Skeletons';
import QuoteFormDateTimeInputs from './QuoteFormDateTimeInputs';
import QuoteFormTop from './QuoteFormTop';
import QuoteFormTermsConditions from './QuoteFormTermsConditions';
import returnDateCalculator from '@/utils/returnDateCalculator';
import { CurrentVehicleProps } from '@/utils/returnDateCalculator/types';
import Link from 'next/link';
import { calculateVehiclePrice } from '@/utils/quoteForm/formCalculations';
import { zonedTimeToUtcFallback } from '@/utils/quoteForm/zonedTimeToUtcFallback';

// On each submit attempt, bring the first field-level error into view. Keyed on
// submitCount (not errors, which also change while typing) so it only fires on a
// submit. The short defer lets Formik's async validation render the error spans
// before we query for the first non-empty one, in document (form) order.
export function ScrollToError() {
    const { submitCount } = useFormikContext();

    useEffect(() => {
        if (submitCount === 0) return;
        const id = window.setTimeout(() => {
            const firstError = document.querySelector(
                '.error-message:not(:empty)'
            );
            firstError?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }, 100);
        return () => window.clearTimeout(id);
    }, [submitCount]);

    return null;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
let mySetFieldValue: any = null;

interface Option {
    label: string | number;
    value: string | number;
}

export interface QuoteFormValues {
    vehicle: CurrentVehicleProps | null;
    collectionDate: Date | null;
    collectionTime: string;
    returnDate: Date | null;
    returnDatePriceCalculator: Date | null;
    returnTime: string;
    waiver: string;
    previousHires: string;
    fullName: string;
    emailAddress: string;
    confirmEmailAddress: string;
    dateOfBirth: string;

    occupation?: string;
    countryOfOrigin?: string;
    phoneNumber: string;
    countryDrivingLicence?: string;
    yearTestPassed?: string;
    address?: string;

    claimConvictionHistory: string;
    claimConvictionComments: string;
    comments: string;
    dobDay: string;
    dobMonth: string;
    dobYear: string;
    numDays: number;
    vehicleTotalPrice: number;
    vehicleMileageAllowance: number;
    vehicleExcessMileageCharge: number;
    excessWaiverPrice?: number;
}

const QuoteForm = ({
    quoteVehicles,
    vehicleId,
    openingTimes,
    closedDates,
    legalContent,
}: any) => {
    const [vehicles] = useState(quoteVehicles);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isPending, startTransition] = useTransition();

    const blankObject = {
        label: '',
        value: '',
        type: '',
        _id: '',
    };

    const [vehicle, setVehicle] = useState<CurrentVehicleProps | null>(
        blankObject
    );

    const [collectionDate, setCollectionDate] = useState<Date | null>(null);
    const [returnDate, setReturnDate] = useState<Date | null>(null);
    const [returnDatePriceCalculator, setReturnDatePriceCalculator] = useState<
        Date | string | number
    >('');
    // const prevUsedSeasonalPrice = useRef<boolean | undefined>(false);
    // const hasShownToast = useRef<boolean>(false);

    const [currentVehicle, setCurrentVehicle] =
        useState<CurrentVehicleProps | null>(null);

    useEffect(() => {
        if (vehicleId) {
            const current = vehicles.find((vehicle: any) => {
                return vehicle._id === vehicleId;
            });

            const vehicleData = {
                label: current?.title || '',
                value: current?._id || '',
                type: current?.type || '',
            };

            if (vehicleData.label) {
                setCurrentVehicle(vehicleData);
                setVehicle(vehicleData);
            }
        } else {
            setCurrentVehicle(vehicle);
        }
        setIsLoading(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleId, vehicles]);

    const initialValues: QuoteFormValues = {
        vehicle: currentVehicle,
        collectionDate: null,
        collectionTime: '',
        returnDate: null,
        returnDatePriceCalculator: null,
        returnTime: '',
        waiver: 'No',
        previousHires: 'No',
        fullName: '',
        emailAddress: '',
        confirmEmailAddress: '',
        dateOfBirth: '',
        occupation: '',
        countryOfOrigin: '',
        phoneNumber: '',
        countryDrivingLicence: '',
        yearTestPassed: '',
        address: '',
        claimConvictionHistory: 'No',
        claimConvictionComments: '',
        comments: '',
        dobDay: '',
        dobMonth: '',
        dobYear: '',
        numDays: 0,
        vehicleTotalPrice: 0,
        vehicleMileageAllowance: 0,
        vehicleExcessMileageCharge: 0,
        excessWaiverPrice: 0,
    };

    // Vehicle drop down options
    const vehiclesOptions = vehicles.map((vehicle: VehicleProps) => {
        return {
            label: vehicle.title,
            value: vehicle._id,
            type: vehicle.type,
        };
    });

    // @ts-expect-error - leaving in for now until full ts errors are addressed
    let numRentalDays: number =
        collectionDate &&
        returnDatePriceCalculator &&
        daysBetween(collectionDate, returnDatePriceCalculator as Date);

    const closedDatesDates = React.useMemo(() => {
        // 1. Guard against non-array/null props to prevent the crash
        if (!closedDates || !Array.isArray(closedDates)) return [];

        // 2. Return an array of strings to match the ReturnDateCalculatorProps interface
        // CMS dates are UK wall-clock 'yyyy-MM-dd' strings — pass them through
        // untouched. A new Date(...).toISOString() round-trip re-frames them in
        // the browser timezone and shifts the day at non-UK offsets.
        return closedDates.map((dateObj: { date: string }) => dateObj.date);
    }, [closedDates]);

    const closedDatesDetails = closedDates;

    useEffect(() => {
        if (collectionDate && returnDate && vehicle) {
            returnDateCalculator({
                returnDate,
                returnDatePriceCalculator: returnDate,
                vehicle,
                numRentalDays,
                closedDates: closedDatesDates,
                closedDatesDetails: closedDatesDetails,
                setReturnDate,
                setReturnDatePriceCalculator,
                mySetFieldValue,
                collectionDate,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionDate, returnDate, vehicle]);

    // Find the vehicle chosen and get all it's props
    const chosenVehicle: VehicleProps = vehicles?.find((v: VehicleProps) => {
        return v?._id === vehicle?._id || v?._id === vehicle?.value;
    });

    // The total price of the vehicle (does not include excess)
    const vehicleTotalPrice =
        vehicle && numRentalDays && chosenVehicle
            ? calculateVehiclePrice(
                  numRentalDays,
                  collectionDate,
                  chosenVehicle.vehicleHireProps
              ).price
            : '';

    // Reset the toast flag whenever returnDate changes
    // useEffect(() => {
    //     hasShownToast.current = false;
    // }, [returnDate]);

    // // Toaster to say that the 4 week rate was applied
    // useEffect(() => {
    //     const result = chosenVehicle
    //         ? calculateVehiclePrice(
    //               numRentalDays,
    //               collectionDate,
    //               chosenVehicle.vehicleHireProps
    //           )
    //         : { usedSeasonalPrice: false };

    //     // Only show the toast the first time usedSeasonalPrice becomes true
    //     if (
    //         result.usedSeasonalPrice &&
    //         !prevUsedSeasonalPrice.current &&
    //         !hasShownToast.current
    //     ) {
    //         toast.success('4 week rate applied');
    //         hasShownToast.current = true;
    //     }
    //     prevUsedSeasonalPrice.current = result.usedSeasonalPrice;
    // }, [numRentalDays, collectionDate, chosenVehicle]);

    // If we used the pricePer4WeeksSummer or pricePer4WeeksWinter instead of the calculated cost because it was cheaper
    // And the rental days is less than 28, then we show the rental days as 28 instead of what it was originally calculated as
    const vehiclePricing = calculateVehiclePrice(
        numRentalDays,
        collectionDate,
        chosenVehicle?.vehicleHireProps
    );

    if (vehiclePricing.usedSeasonalPrice) {
        if (numRentalDays < 28) {
            numRentalDays = 28;
        } else if (numRentalDays >= 28 && numRentalDays <= 83) {
            numRentalDays = 84;
        }
    }

    if (!vehicleId && vehicles.length === 0) {
        return null;
    }

    // The price of the excess mileage charge
    const vehicleExcessMileagePrice = vehicle
        ? chosenVehicle && chosenVehicle?.vehicleHireProps.excessMileageCharge
        : '';

    // The standard insurance excess
    const standardInsuranceExcess = vehicle ? (
        chosenVehicle?.vehicleHireProps.standardInsuranceExcess ? (
            `£${chosenVehicle?.vehicleHireProps.standardInsuranceExcess.toFixed(2)}`
        ) : (
            <div className="loading text-xs">Waiting</div>
        )
    ) : (
        ''
    );

    // The cdw insurance excess display
    const cdwInsuranceExcess = vehicle ? (
        chosenVehicle?.vehicleHireProps.cdwInsuranceExcess ? (
            `£${chosenVehicle.vehicleHireProps.cdwInsuranceExcess.toFixed(2)}`
        ) : (
            <div className="loading text-xs">Waiting</div>
        )
    ) : (
        ''
    );

    // Vehicle mileage allowance (currently they're all the same on the site so far)
    const vehicleMileageAllowance =
        vehicle && chosenVehicle && numRentalDays < 28
            ? chosenVehicle?.vehicleHireProps.mileageAllowance * numRentalDays
            : (chosenVehicle?.vehicleHireProps.mileageAllowance4Weeks / 28) *
              numRentalDays;

    /**
     * Calculates the excess waiver price based on the chosen vehicle and rental days
     * @returns the excess waiver price as a string
     */
    const calculateExcessWaiverPrice = (waiverOptionValue: string): number => {
        if (waiverOptionValue !== 'Yes' || !numRentalDays) {
            // If the waiver is not selected or the rental days is invalid, return 0
            return 0;
        }

        const { cdwDaily, cdw6Days, cdwWeekly, cdw4Weeks } =
            chosenVehicle.vehicleHireProps;

        // If the rental days is between 1 and 5 days, use the daily CDW price
        if (numRentalDays < 6) {
            return cdwDaily * numRentalDays;
        }

        // If the rental days is exactly 6 days, use the 6 day CDW price
        if (numRentalDays === 6) {
            return cdw6Days;
        }

        // If the rental days is between 7 days and 27 days inclusive, use the weekly CDW price
        if (numRentalDays > 6 && numRentalDays < 28) {
            return Math.round(
                (((cdwWeekly / 7) * numRentalDays + Number.EPSILON) * 100) / 100
            );
        }

        // If the rental days is more than 28, use the 4-week CDW price
        if (numRentalDays > 27) {
            return Math.round(
                (((cdw4Weeks / 28) * numRentalDays + Number.EPSILON) * 100) /
                    100
            );
        } else {
            // If the rental days is invalid, return 0
            return 0;
        }
    };

    // When there's an error, we show an error at
    // the top of the page as opposed under each form element
    const ErrorBlock = () => {
        return (
            <div className="text-red-600 text-xs p-2 mb-2 flex items-center bg-red-100 text-center">
                <span>All fields are required</span>
            </div>
        );
    };

    // If we have a vehicleId in the url,
    // and no vehicle fetched, then show a loading skeleton
    if (vehicleId && !currentVehicle) {
        return <FormSkeleton />;
    }

    return (
        <PageMainCard>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur={true}
                onSubmit={async (
                    values,
                    { resetForm, setFieldError, setSubmitting }
                ) => {
                    // Scroll to top immediately to show the user feedback area
                    window.scrollTo({ top: 0, behavior: 'smooth' });

                    startTransition(async () => {
                        try {
                            // Create FormData from Formik values
                            const formData = new FormData();

                            // Handle vehicle object - stringify for transmission
                            formData.append(
                                'vehicle',
                                JSON.stringify(values.vehicle)
                            );

                            // Handle dates - convert to ISO strings interpreted as Europe/London
                            // We interpret the picked wall-clock time as UK local time
                            // and convert that to the corresponding UTC instant to send.
                            const collectionIso = values.collectionDate
                                ? zonedTimeToUtcFallback(
                                      format(
                                          values.collectionDate,
                                          'yyyy-MM-dd HH:mm'
                                      ),
                                      'Europe/London'
                                  ).toISOString()
                                : '';

                            const returnIso = values.returnDate
                                ? zonedTimeToUtcFallback(
                                      format(
                                          values.returnDate,
                                          'yyyy-MM-dd HH:mm'
                                      ),
                                      'Europe/London'
                                  ).toISOString()
                                : '';

                            formData.append('collectionDate', collectionIso);
                            formData.append('returnDate', returnIso);

                            // Handle string fields
                            formData.append('fullName', values.fullName || '');
                            formData.append(
                                'emailAddress',
                                values.emailAddress || ''
                            );
                            formData.append(
                                'confirmEmailAddress',
                                values.confirmEmailAddress || ''
                            );
                            formData.append(
                                'dateOfBirth',
                                values.dateOfBirth || ''
                            );
                            formData.append(
                                'phoneNumber',
                                values.phoneNumber || ''
                            );
                            formData.append(
                                'claimConvictionComments',
                                values.claimConvictionComments || ''
                            );
                            formData.append('comments', values.comments || '');

                            // Date of birth components
                            formData.append('dobDay', values.dobDay || '');
                            formData.append('dobMonth', values.dobMonth || '');
                            formData.append('dobYear', values.dobYear || '');

                            // Radio button fields
                            formData.append('waiver', values.waiver || '');
                            formData.append(
                                'previousHires',
                                values.previousHires || ''
                            );
                            formData.append(
                                'claimConvictionHistory',
                                values.claimConvictionHistory || ''
                            );

                            // Conditional fields
                            formData.append(
                                'occupation',
                                values.occupation || ''
                            );
                            formData.append(
                                'countryOfOrigin',
                                values.countryOfOrigin || ''
                            );
                            formData.append(
                                'countryDrivingLicence',
                                values.countryDrivingLicence || ''
                            );
                            formData.append(
                                'yearTestPassed',
                                values.yearTestPassed || ''
                            );
                            formData.append('address', values.address || '');

                            // Calculated fields - add the ones you were setting before submission
                            formData.append(
                                'numDays',
                                (numRentalDays || 0).toString()
                            );
                            formData.append(
                                'vehicleTotalPrice',
                                (vehicleTotalPrice || 0).toString()
                            );
                            formData.append(
                                'vehicleMileageAllowance',
                                (vehicleMileageAllowance || 0).toString()
                            );
                            formData.append(
                                'vehicleExcessMileageCharge',
                                (vehicleExcessMileagePrice || 0).toString()
                            );
                            formData.append(
                                'excessWaiverPrice',
                                (
                                    calculateExcessWaiverPrice(values.waiver) ||
                                    0
                                ).toString()
                            );

                            // Append honeypot field value from DOM
                            const honeypotInput = document.getElementById(
                                HONEYPOT_FIELD
                            ) as HTMLInputElement | null;
                            formData.append(
                                HONEYPOT_FIELD,
                                honeypotInput?.value || ''
                            );
                            formData.append(
                                'browserTimezone',
                                Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone || ''
                            );

                            // Call server action
                            const result = await submitQuoteForm(formData);

                            if (result.isBot) {
                                // Silently swallow — bot thinks it succeeded, we do nothing
                                return;
                            }

                            if (result.success) {
                                toast.success(
                                    "Thank you for your booking request. We will email you shortly to confirm your booking as the website does not check availability. We will always reply by email as soon as possible during office hours - if you haven't had a response, please check your spam and junk emails.",
                                    {
                                        duration: 10000,
                                    }
                                );
                                resetForm();
                            } else {
                                // Handle validation errors from server
                                if (result.fieldErrors) {
                                    Object.entries(result.fieldErrors).forEach(
                                        ([field, message]) => {
                                            setFieldError(
                                                field,
                                                message as string
                                            );
                                        }
                                    );
                                }

                                // Show error toast
                                toast.error(
                                    result.error ||
                                        'Something went wrong. Please try again.',
                                    {
                                        duration: 6000,
                                    }
                                );

                                // Log errors for debugging
                                if (result.validationErrors) {
                                    console.log(
                                        'Validation errors:',
                                        result.validationErrors
                                    );
                                }
                            }
                        } catch (error) {
                            console.error('Form submission error:', error);
                            toast.error(
                                'An unexpected error occurred. Please try again.',
                                {
                                    duration: 6000,
                                }
                            );
                        } finally {
                            setSubmitting(false);
                        }
                    });
                }}
            >
                {({
                    handleSubmit,
                    errors,
                    values,
                    isSubmitting,
                    setFieldValue,
                }) => {
                    mySetFieldValue = setFieldValue;

                    const excessWaiverPrice = calculateExcessWaiverPrice(
                        values.waiver
                    );

                    const errorsExist = Object.keys(errors).length > 0;

                    // 1. Ensure vehicleChange signature matches what DropdownSelect expects: (_option: Option | null) => void
                    const vehicleChange = (option: Option | null) => {
                        // 2. Handle null (if the user clears the dropdown)
                        if (!option) return;

                        /** * 3. Map the Option to CurrentVehicleProps.
                         * Since Option has { label, value } and CurrentVehicleProps likely
                         * shares these, we cast it to satisfy the setVehicle state setter.
                         */
                        const vehicleData =
                            option as unknown as CurrentVehicleProps;

                        // Set the selected vehicle in state
                        setVehicle(vehicleData);

                        // 4. Existing business logic for price recalculation
                        if (collectionDate && returnDate) {
                            // Store the current return date safely
                            const currentReturnDate = new Date(
                                returnDate.getTime()
                            );

                            // Clear the return date state to trigger the lifecycle "reset"
                            setReturnDate(null);

                            // Use setTimeout to ensure the clearing state update is processed
                            setTimeout(() => {
                                // Restore the return date to trigger a full recalculation
                                setReturnDate(currentReturnDate);
                                setReturnDatePriceCalculator(currentReturnDate);
                            }, 0);
                        }
                    };

                    const totalRentalCost = vehicleTotalPrice
                        ? `£${(vehicleTotalPrice + excessWaiverPrice).toFixed(2)}`
                        : 0;

                    return isLoading || vehicles.length === 0 ? (
                        <FormSkeleton />
                    ) : (
                        <form onSubmit={handleSubmit} className="w-full">
                            <ScrollToError />
                            <QuoteFormTop openingTimes={openingTimes} />

                            {isSubmitting && errorsExist && <ErrorBlock />}
                            <div className="flex text-md italic items-center required text-gray-500 mb-2">
                                Required
                            </div>
                            <FormRow
                                label={`Select a vehicle:`}
                                content={
                                    <DropdownSelect
                                        name="vehicle"
                                        options={vehiclesOptions}
                                        onChange={vehicleChange}
                                        placeholder="Select a vehicle"
                                    />
                                }
                            />

                            <QuoteFormDateTimeInputs
                                setCollectionDate={setCollectionDate}
                                setReturnDate={setReturnDate}
                                setReturnDatePriceCalculator={
                                    setReturnDatePriceCalculator
                                }
                                closedDates={closedDates}
                            />

                            <input
                                type="hidden"
                                name="numDays"
                                value={
                                    numRentalDays
                                        ? (numRentalDays as number)
                                        : '' // empty string to avoid an input having a null value if no data is available
                                }
                            />

                            <NonFormRow
                                label="Rental period:"
                                content={
                                    numRentalDays ? (
                                        `${numRentalDays} day${numRentalDays > 1 ? 's' : ''}`
                                    ) : (
                                        <div className="loading text-xs">
                                            Waiting
                                        </div>
                                    )
                                }
                            />

                            <NonFormRow
                                label="Inclusive price:"
                                content={
                                    vehicleTotalPrice ? (
                                        `£${vehicleTotalPrice.toFixed(2)}`
                                    ) : (
                                        <div className="loading text-xs">
                                            Waiting
                                        </div>
                                    )
                                }
                            />

                            <HoneypotField />

                            <FormDivider />

                            <FormRow
                                label="Excess Waiver (reduce Insurance
                                            Excess in the event of a claim from
                                            £500 to £200)?"
                                content={
                                    <RadioButtonGroup
                                        name="waiver"
                                        options={['Yes', 'No']}
                                    />
                                }
                            />

                            <NonFormRow
                                label="Excess Waiver Cost:"
                                content={
                                    numRentalDays ? (
                                        `£${excessWaiverPrice.toFixed(2)}`
                                    ) : (
                                        <div className="loading text-xs">
                                            Waiting
                                        </div>
                                    )
                                }
                            />

                            <FormDivider />

                            <NonFormRow
                                label="Insurance Excess:"
                                content={
                                    values.waiver === 'Yes'
                                        ? cdwInsuranceExcess
                                        : standardInsuranceExcess
                                }
                            />

                            <NonFormRow
                                label="Total Rental Cost:"
                                content={
                                    totalRentalCost ? (
                                        totalRentalCost
                                    ) : (
                                        <div className="loading text-xs">
                                            Waiting
                                        </div>
                                    )
                                }
                            />

                            <NonFormRow
                                label="Included Mileage Allowance:"
                                content={
                                    vehicleMileageAllowance && numRentalDays ? (
                                        `${vehicleMileageAllowance.toFixed(0)} miles`
                                    ) : (
                                        <div className="loading text-xs">
                                            Waiting
                                        </div>
                                    )
                                }
                                className="mt-8"
                            />

                            <NonFormRow
                                label="Excess mileage:"
                                content={
                                    vehicleExcessMileagePrice ? (
                                        `${(vehicleExcessMileagePrice * 100).toFixed(0)}p per mile`
                                    ) : (
                                        <div className="loading text-xs">
                                            Waiting
                                        </div>
                                    )
                                }
                            />

                            <FormDivider />

                            <FormRow
                                label="Have you hired from us in the last 5
                                            years?:"
                                content={
                                    <RadioButtonGroup
                                        name="previousHires"
                                        options={['Yes', 'No']}
                                    />
                                }
                            />

                            <FormRow
                                label="Full name:"
                                content={
                                    <TextInput
                                        name="fullName"
                                        placeholder="Your full name"
                                    />
                                }
                            />

                            <FormRow
                                label="Email address:"
                                content={
                                    <TextInput
                                        name="emailAddress"
                                        placeholder="Your email address"
                                    />
                                }
                            />

                            <FormRow
                                label="Confirm email address:"
                                content={
                                    <TextInput
                                        name="confirmEmailAddress"
                                        placeholder="Confirm your email address"
                                    />
                                }
                            />

                            <FormRow
                                label="Date of birth:"
                                content={<DateOfBirth name="dateOfBirth" />}
                            />

                            {values.previousHires === 'No' ? (
                                <>
                                    <FormRow
                                        label="Occupation:"
                                        content={
                                            <TextInput
                                                name="occupation"
                                                placeholder="Your occupation"
                                            />
                                        }
                                    />
                                    <FormRow
                                        label="Country of origin:"
                                        content={
                                            <TextInput
                                                name="countryOfOrigin"
                                                placeholder="Your country of origin"
                                            />
                                        }
                                    />
                                    <FormRow
                                        label="Best contact number:"
                                        content={
                                            <TextInput
                                                name="phoneNumber"
                                                placeholder="Your phone number"
                                            />
                                        }
                                    />
                                    <FormRow
                                        label="Country driving licence issued in:"
                                        content={
                                            <TextInput
                                                name="countryDrivingLicence"
                                                placeholder="Your driving licence country"
                                            />
                                        }
                                    />
                                    <FormRow
                                        label="Year driving test passed:"
                                        content={
                                            <TextInput
                                                name="yearTestPassed"
                                                placeholder="The year you passed your test"
                                            />
                                        }
                                    />
                                    <FormRow
                                        label="Your address:"
                                        content={
                                            <TextInput
                                                container="textarea"
                                                name="address"
                                                placeholder="Your address"
                                            />
                                        }
                                    />
                                </>
                            ) : (
                                <FormRow
                                    label="Best contact number:"
                                    content={
                                        <TextInput
                                            name="phoneNumber"
                                            placeholder="Your phone number"
                                        />
                                    }
                                />
                            )}

                            <FormRow
                                label="Motor insurance claims in the past 3
                                            years (regardless of fault) or motor
                                            convictions in past 5 years?:"
                                content={
                                    <RadioButtonGroup
                                        name="claimConvictionHistory"
                                        options={['Yes', 'No']}
                                    />
                                }
                            />
                            {values.claimConvictionHistory === 'Yes' ? (
                                <FormRow
                                    label="Details of insurance claims or motoring convictions:"
                                    content={
                                        <TextInput
                                            name="claimConvictionComments"
                                            placeholder="Please provide details of your insurance claims or motor convictions."
                                            container="textarea"
                                        />
                                    }
                                    required={
                                        values.claimConvictionHistory === 'Yes'
                                    }
                                />
                            ) : null}

                            <FormRow
                                label="Any Question's or comments?:"
                                content={
                                    <TextInput
                                        name="comments"
                                        placeholder="e.g. please mention if you would like to have an additional driver so we can discuss this with you on reply."
                                        container="textarea"
                                    />
                                }
                                required={false}
                            />

                            <FormDivider />

                            <p className="text-md text-center font-bold">
                                Please note the prices are for the UK only.
                            </p>

                            <p className="text-md text-center mb-2 font-bold">
                                We are currently unable to offer overseas
                                rentals.
                            </p>

                            <FormDivider />

                            <QuoteFormTermsConditions
                                legalContent={legalContent}
                            />

                            <p className="text-sm text-center mb-2">
                                By clicking submit you confirm you have read our{' '}
                                <Link href="/legal/terms" className="underline">
                                    Terms & Conditions
                                </Link>{' '}
                                and what documents you will need to
                                bring/provide.
                            </p>

                            <ValuesDisplay values={values} />

                            <div className="flex justify-center">
                                <div className="w-full justify-end flex">
                                    <Button
                                        label={
                                            isPending || isSubmitting
                                                ? 'Submitting...'
                                                : 'Submit'
                                        }
                                        disabled={isPending || isSubmitting}
                                        className="!text-3xl mx-auto mb-4 mt-2"
                                    />
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </PageMainCard>
    );
};

export default QuoteForm;
